// client/src/components/auth/RequirePermission.tsx

import type { ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { AccessDenied } from "@/pages/AccessDenied";
import type { Resource, Action } from "@shared/types/permissions";

interface RequirePermissionProps {
  /** Resource to gate on. */
  resource: Resource;
  /** Minimum action required to render children. */
  action: Action;
  children: ReactNode;
}

/**
 * Permission-based route guard.
 *
 * Renders `children` only when the current user's `hasPermission(resource, action)`
 * is true. Otherwise renders `<AccessDenied />`.
 *
 * EP-07 / SA-00 (ADR 011): replaces role-string checks like `role === 'property_manager'`
 * so the same component works for PM, SA, and any future role that grants the
 * required permission. The SSOT is `DEFAULT_ROLE_PERMISSIONS[role]`.
 *
 * @example
 *   <RequirePermission resource="users" action="manage">
 *     <UserManagement />
 *   </RequirePermission>
 *
 * @see useCanWrite / useIsReadOnly for in-component gating (same SSOT).
 */
export function RequirePermission({
  resource,
  action,
  children,
}: RequirePermissionProps) {
  const { hasPermission, isLoading } = useAuth();

  // Match the loading behavior of RequirePM: render nothing while auth resolves.
  if (isLoading) {
    return null;
  }

  if (!hasPermission(resource, action)) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}